/**
 * PaywallModal — renders the friendly upgrade prompt whenever any HTTP
 * 402 fires anywhere in the app (see /lib/paywall.js).
 *
 * Headline mirrors the backend ``reason`` verbatim so copy can be tuned
 * without a deploy. The "Required tier" is computed from the gate type:
 * if the backend told us ``required_tier`` we use it, otherwise we pick
 * the cheapest paid tier above ``current_tier``.
 *
 * One instance is mounted by ``App.js`` and listens for ``paywall:open``
 * events from anywhere in the tree. No context wrapping required.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Lock, ArrowRight, Sparkles, Crown } from 'lucide-react';
import { TIERS, TIER_ORDER, tierMeets } from '../lib/tiers';
import { onPaywall } from '../lib/paywall';

const ICONS_BY_TIER = {
  student: Sparkles,
  scholar: Crown,
  pro: Crown,
};

const PaywallModal = () => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => onPaywall((detail) => {
    setPayload(detail);
    setOpen(true);
  }), []);

  if (!payload) return null;

  const currentTier = payload.current_tier || 'free';
  // Pick the cheapest paid tier above the user's current one if the
  // backend didn't tell us explicitly.
  let requiredTier = payload.required_tier;
  if (!requiredTier) {
    requiredTier = TIER_ORDER.find((t) => t !== 'free' && !tierMeets(currentTier, t)) || 'student';
  }
  const tierInfo = TIERS[requiredTier] || TIERS.student;
  const Icon = ICONS_BY_TIER[requiredTier] || Sparkles;

  const limitKind = payload.limit_kind || payload.feature || 'this feature';
  const showUsage = payload.error === 'tier_limit_reached' && typeof payload.current === 'number';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-md bg-[var(--isp-glass)] border-[var(--isp-glass-border)]"
        data-testid="paywall-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#002FA7]/10">
            <Lock className="w-7 h-7 text-[#002FA7]" />
          </div>
          <DialogTitle className="text-center text-2xl font-bold text-[var(--isp-heading)]">
            {payload.error === 'tier_feature_locked'
              ? `Unlock ${limitKind}`
              : `You've maxed out ${limitKind}`}
          </DialogTitle>
        </DialogHeader>

        <p
          className="text-sm text-[var(--isp-body)] text-center leading-relaxed mt-1"
          data-testid="paywall-reason"
        >
          {payload.reason}
        </p>

        {showUsage && (
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[var(--isp-muted)]">
            <span className="font-mono">{payload.current}</span>
            <span>/</span>
            <span className="font-mono">{payload.limit === 'unlimited' ? '∞' : payload.limit}</span>
            <span className="ml-1">used on your {currentTier} plan</span>
          </div>
        )}

        <div className="mt-5 p-4 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#002FA7] flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[var(--isp-muted)] font-semibold">Upgrade to</p>
              <p className="text-base font-bold text-[var(--isp-heading)]">
                {tierInfo.name} — ${tierInfo.priceUsd}/mo
              </p>
            </div>
          </div>
          <ul className="space-y-1.5">
            {(tierInfo.highlights || []).slice(0, 4).map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-[var(--isp-body)]">
                <span className="text-[#002FA7] mt-0.5">✓</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="flex-1 text-[var(--isp-muted)] hover:text-[var(--isp-heading)]"
            data-testid="paywall-dismiss"
          >
            Not now
          </Button>
          <Link to={`/pricing?tier=${requiredTier}`} className="flex-1" onClick={() => setOpen(false)}>
            <Button
              className="w-full isp-btn-primary gap-2"
              data-testid="paywall-upgrade-cta"
            >
              Upgrade to {tierInfo.name}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
